import React, { useState, useEffect } from "react";
import { FaArrowLeft, FaCalendar, FaBook, FaGraduationCap, FaClipboardList, FaChevronRight } from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";
import { useNavigate } from "react-router-dom";


const SelectQuiz = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [selectedFilters, setSelectedFilters] = useState({
        year: "All Years",
        subject: "All Subjects",
        mode: "All Modes",
        answerType: "All Answer Types",
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [userResults, setUserResults] = useState([]);
    const [promoResults, setPromoResults] = useState([]);

    useEffect(() => {
        fetchBothHistories();
    }, []);

    useEffect(() => {
        // Reset filters when tab changes
        setSelectedFilters({
            year: "All Years",
            subject: "All Subjects",
            mode: "All Modes",
            answerType: "All Answer Types",
        });
    }, [activeTab]);

    const fetchBothHistories = async () => {
        setLoading(true);
        await Promise.all([fetchUserResults(), fetchPromoResults()]);
        setLoading(false);
    };

    const fetchUserResults = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/answer/user`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch user results.");
            }

            const resultData = await response.json();
            setUserResults(resultData.data || []);
        } catch (error) {
            console.error("Error fetching results:", error.message);
            setError("Failed to fetch user results.");
        }
    };

    const fetchPromoResults = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/answer-promo/user`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch promo results.");
            }

            const resultData = await response.json();

            const normalizedData = (resultData.data || []).map((item) => {
                const q = item.question || {};

                return {
                    answerType: item.answerType || "personal",
                    createdAt: item.createdAt,
                    is_correct: item.is_correct || 0,
                    mode: item.mode || "exam",
                    question_id: q.id || item.question_id,
                    quiz: { class: q.class || "" },
                    correctAnswer: q.correctAnswer,
                    explanation: q.explanation,
                    id: q.id,
                    image: q.image || null,
                    level: q.level || "Easy",
                    option_a: q.option_a,
                    option_b: q.option_b,
                    option_c: q.option_c,
                    option_d: q.option_d,
                    question: q.question,
                    subject: q.subject || item.subject,
                    topic: q.topic || item.topic || "",
                    selected_option: item.selected_option || null,
                    test: item.test || { id: item.test_id, createdAt: item.createdAt },
                    test_id: item.test?.id || item.test_id,
                    user_answer_id: item.user_answer_id || 0,
                    user_id: item.user_id,
                };
            });

            setPromoResults(normalizedData);
        } catch (error) {
            console.error("Error fetching promo results:", error.message);
        }
    };



    const getCurrentData = () => {
        return activeTab === 0 ? userResults : promoResults;
    };

    const handleQuizSelection = (subject, mode, testId) => {
        const currentData = getCurrentData();
        const filteredResults = currentData.filter(
            (item) =>
                item.subject === subject && item.mode === mode && item.test_id === testId
        );

        if (filteredResults.length === 0) {
            setError("No results found for the selected criteria.");
            return;
        }

        // In your actual app, replace this with: navigate("/result", { state: { result: filteredResults } });
        console.log("Navigate to result with:", filteredResults);
        navigate("/result", { state: { result: filteredResults } });
    };
    const navigate = useNavigate()

    const goBack = () => {
        navigate('/dashboard')
    };

    const getUniqueExamData = () => {
        const currentData = getCurrentData();
        const uniqueExamDataMap = new Map();

        currentData.forEach((item) => {
            const uniqueKey = `${item.subject}_${item.mode}_${item.test_id}_${item.answerType}`;
            if (!uniqueExamDataMap.has(uniqueKey)) {
                uniqueExamDataMap.set(uniqueKey, {
                    subject: item.subject,
                    mode: item.mode,
                    answerType: item.answerType,
                    testId: item.test_id,
                    createdAt: item.createdAt,
                });
            }
        });

        return Array.from(uniqueExamDataMap.values());
    };

    const getFilterOptions = (key) => {
        const currentData = getCurrentData();
        const uniqueValues = new Set(
            currentData.map((item) =>
                key === "year"
                    ? new Date(item.createdAt).getFullYear()
                    : key === "subject"
                        ? item.subject
                        : key === "mode"
                            ? item.mode
                            : key === "answerType"
                                ? item.answerType
                                : null
            )
        );
        return Array.from(uniqueValues).filter(Boolean).sort();
    };

    const filteredExamData = getUniqueExamData().filter((data) => {
        const year = new Date(data.createdAt).getFullYear();
        return (
            (selectedFilters.year === "All Years" || year === parseInt(selectedFilters.year)) &&
            (selectedFilters.subject === "All Subjects" || data.subject === selectedFilters.subject) &&
            (selectedFilters.mode === "All Modes" || data.mode === selectedFilters.mode) &&
            (selectedFilters.answerType === "All Answer Types" ||
                data.answerType === selectedFilters.answerType)
        );
    });

    const FilterChip = ({ icon: Icon, value, options, onChange }) => (
        <div className="relative">
            <select
                className="appearance-none bg-white border border-gray-200 rounded-lg px-10 py-2 pr-8 text-sm font-medium text-gray-700 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer shadow-sm"
                value={value}
                onChange={(e) => onChange(e.target.value)}
            >
                {options.map((option) => (
                    <option key={option} value={option}>
                        {option}
                    </option>
                ))}
            </select>
            <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>
        </div>
    );

    const HistoryCard = ({ item }) => {
        const date = item.createdAt ? new Date(item.createdAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        }) : "N/A";

        return (
            <div
                className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all cursor-pointer group"
                onClick={() => handleQuizSelection(item.subject, item.mode, item.testId)}
            >
                <div className="flex items-start gap-3">
                    <div className="bg-blue-50 rounded-lg p-3 flex-shrink-0">
                        <FaClipboardList className="text-blue-600 text-xl" />
                    </div>
                    <div className="flex-grow min-w-0">
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex-grow min-w-0">
                                <h3 className="font-semibold text-gray-900 text-sm truncate">
                                    {item.subject}
                                </h3>
                                <p className="text-xs text-gray-500 mt-1">{date}</p>
                            </div>
                            <FaChevronRight className="text-gray-400 text-sm flex-shrink-0 mt-1 group-hover:text-blue-600 transition-colors" />
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 border border-blue-100 rounded-md text-xs text-gray-700 font-medium">
                                <FaGraduationCap className="text-blue-600 text-xs" />
                                {item.mode}
                            </span>
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 border border-blue-100 rounded-md text-xs text-gray-700 font-medium capitalize">
                                {item.answerType}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            {/* Decorative Background Gradient */}
            <div className="fixed top-0 right-0 w-96 h-96 bg-blue-100 rounded-full filter blur-3xl opacity-20 -z-10 transform translate-x-1/2 -translate-y-1/2" />

            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-6xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={goBack}
                            className="flex items-center gap-2 text-gray-700 hover:text-blue-600 font-medium transition-colors"
                        >
                            <div className="bg-gray-100 rounded-full p-2">
                                <FaArrowLeft className="text-sm" />
                            </div>
                        </button>
                        <h1 className="text-3xl font-bold text-blue-600 absolute left-1/2 transform -translate-x-1/2">
                            History
                        </h1>
                        <div className="w-10" />
                    </div>
                    <p className="text-center text-lg text-gray-600 mt-2">
                        Review your past exam and quiz attempts
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="max-w-6xl mx-auto w-full px-4 mt-4">
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-1 inline-flex w-full max-w-md mx-auto">
                    <button
                        className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all ${activeTab === 0
                            ? "bg-blue-600 text-white shadow-sm"
                            : "text-gray-700 hover:bg-blue-100"
                            }`}
                        onClick={() => setActiveTab(0)}
                    >
                        Regular Exams
                    </button>
                    <button
                        className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all ${activeTab === 1
                            ? "bg-blue-600 text-white shadow-sm"
                            : "text-gray-700 hover:bg-blue-100"
                            }`}
                        onClick={() => setActiveTab(1)}
                    >
                        WAEC/JAMB
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <main className="flex-grow max-w-6xl mx-auto w-full px-4 py-6">
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <>
                        {/* Filters */}
                        <div className="flex flex-wrap gap-3 justify-center mb-6">
                            <FilterChip
                                icon={FaCalendar}
                                value={selectedFilters.year}
                                options={["All Years", ...getFilterOptions("year")]}
                                onChange={(value) =>
                                    setSelectedFilters((prev) => ({ ...prev, year: value }))
                                }
                            />
                            <FilterChip
                                icon={FaBook}
                                value={selectedFilters.subject}
                                options={["All Subjects", ...getFilterOptions("subject")]}
                                onChange={(value) =>
                                    setSelectedFilters((prev) => ({ ...prev, subject: value }))
                                }
                            />
                            <FilterChip
                                icon={FaGraduationCap}
                                value={selectedFilters.mode}
                                options={["All Modes", ...getFilterOptions("mode")]}
                                onChange={(value) =>
                                    setSelectedFilters((prev) => ({ ...prev, mode: value }))
                                }
                            />
                            <FilterChip
                                icon={FaClipboardList}
                                value={selectedFilters.answerType}
                                options={["All Answer Types", ...getFilterOptions("answerType")]}
                                onChange={(value) =>
                                    setSelectedFilters((prev) => ({ ...prev, answerType: value }))
                                }
                            />
                        </div>

                        {/* History Cards */}
                        {filteredExamData.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20">
                                <div className="bg-gray-100 rounded-full p-8 mb-4">
                                    <FaClipboardList className="text-gray-300 text-6xl" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    No exam history found
                                </h3>
                                <p className="text-sm text-gray-500">
                                    Your completed exams will appear here
                                </p>
                            </div>
                        ) : (
                            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                                {filteredExamData.map((data, index) => (
                                    <HistoryCard key={index} item={data} />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </main>

            {/* Footer */}
            <footer className="text-center text-gray-300 py-4 bg-black bg-opacity-40 mt-auto">
                <p>
                    &copy; {new Date().getFullYear()} TeeChaa CBT Application. All rights
                    reserved.
                </p>
            </footer>
        </div>
    );
};

export default SelectQuiz;